import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  TrendingUp, Package, Boxes, ClipboardList, Settings, MessageSquare,
  Pencil, Trash2, Plus, X, ShoppingBag, Users, Wallet, LayoutDashboard, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, apiError } from "../lib/api";
import { fmtPrice, STATUS_LABELS, STATUS_STYLES, ORDER_STATUSES } from "../lib/shop";
import { ImageField } from "../components/ImageField";

const TABS = [
  { id: "stats", label: "Statistici", icon: TrendingUp },
  { id: "produse", label: "Produse", icon: Package },
  { id: "categorii", label: "Categorii", icon: Boxes },
  { id: "comenzi", label: "Comenzi", icon: ClipboardList },
  { id: "retrageri", label: "Retrageri", icon: RotateCcw },
  { id: "conturi", label: "Conturi", icon: Users },
  { id: "continut", label: "Conținut site", icon: Settings },
  { id: "mesaje", label: "Mesaje", icon: MessageSquare },
];

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none transition-colors focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20";
const labelCls = "mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500";

const EMPTY_PRODUCT = { name: "", category: "", price: "", old_price: "", tag: "", stock: 0, volume: "", image: "", description: "", aroma: "", sku: "", usage: "", composition: "", rating: 4.8, reviews_count: 0, featured: false, hidden: false, free_shipping: false };
const EMPTY_CATEGORY = { name: "", description: "", tag: "", image: "", usage: "", order: 99 };

function StatsTab() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setRecent(r.data.slice(0, 5))).catch(() => {});
  }, []);
  if (!stats) return <div className="h-64 animate-pulse rounded-3xl bg-slate-100" />;
  const cards = [
    { icon: Wallet, label: "Total vânzări", value: fmtPrice(stats.total_sales) },
    { icon: ShoppingBag, label: "Comenzi astăzi", value: stats.orders_today },
    { icon: Package, label: "Produse în stoc", value: stats.products_in_stock },
    { icon: Users, label: "Clienți înregistrați", value: stats.customers },
  ];
  return (
    <div data-testid="admin-stats-tab">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-3xl border border-slate-200/80 bg-white p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100">
              <c.icon className="h-5 w-5 text-emerald-600" />
            </span>
            <p className="mt-4 font-display text-2xl font-extrabold text-slate-900">{c.value}</p>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{c.label}</p>
          </div>
        ))}
      </div>
      <h3 className="mt-10 font-display text-lg font-bold text-slate-900">Comenzi recente</h3>
      <div className="mt-4 flex flex-col gap-3">
        {recent.length === 0 && <p className="text-sm text-slate-400">Nicio comandă încă.</p>}
        {recent.map((o) => (
          <div key={o.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200/80 bg-white px-5 py-4">
            <div>
              <p className="text-sm font-bold text-slate-900">{o.number} — {o.customer.name}</p>
              <p className="text-xs text-slate-400">{new Date(o.created_at).toLocaleString("ro-RO")}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${STATUS_STYLES[o.status]}`}>{STATUS_LABELS[o.status]}</span>
              <span className="text-sm font-extrabold text-slate-900">{fmtPrice(o.total)}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [aromaOptions, setAromaOptions] = useState(["Ocean Breeze", "Floral Fresh"]);

  const load = useCallback(() => {
    api.get("/products", { params: { include_hidden: true } }).then((r) => setProducts(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/content").then((r) => {
      const names = (r.data.arome || []).map((a) => a.name).filter(Boolean);
      if (names.length) setAromaOptions(names);
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...editing.form,
      price: parseFloat(editing.form.price) || 0,
      old_price: editing.form.old_price === "" || editing.form.old_price === null ? null : parseFloat(editing.form.old_price),
      stock: parseInt(editing.form.stock, 10) || 0,
      rating: parseFloat(editing.form.rating) || 4.8,
      reviews_count: parseInt(editing.form.reviews_count, 10) || 0,
    };
    try {
      if (editing.id) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing.id ? "Produs actualizat" : "Produs adăugat");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Ștergi produsul „${p.name}"?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Produs șters");
    load();
  };

  const setF = (k, v) => setEditing((s) => ({ ...s, form: { ...s.form, [k]: v } }));

  return (
    <div data-testid="admin-products-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-slate-900">Produse ({products.length})</h3>
        <button
          onClick={() => setEditing({ id: null, form: { ...EMPTY_PRODUCT, category: categories[0]?.name || "" } })}
          className="flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-xs font-bold text-white transition-colors hover:bg-emerald-700"
          data-testid="admin-product-add-button"
        >
          <Plus className="h-4 w-4" /> Adaugă produs
        </button>
      </div>

      {editing && (
        <form onSubmit={save} className="mt-5 rounded-3xl border-2 border-emerald-500/40 bg-white p-6" data-testid="admin-product-form">
          <div className="flex items-center justify-between">
            <h4 className="font-display text-base font-bold">{editing.id ? "Editează produsul" : "Produs nou"}</h4>
            <button type="button" onClick={() => setEditing(null)} aria-label="Închide" data-testid="admin-product-form-close"><X className="h-5 w-5 text-slate-400" /></button>
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className={labelCls}>Nume produs</label>
              <input required value={editing.form.name} onChange={(e) => setF("name", e.target.value)} className={inputCls} data-testid="admin-product-name-input" />
            </div>
            <div>
              <label className={labelCls}>Categorie</label>
              <select required value={editing.form.category} onChange={(e) => setF("category", e.target.value)} className={inputCls} data-testid="admin-product-category-select">
                <option value="">Alege categoria</option>
                {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Volum / gramaj</label>
              <input value={editing.form.volume} onChange={(e) => setF("volume", e.target.value)} className={inputCls} placeholder="Ex: 2 L (40 de spălări)" data-testid="admin-product-volume-input" />
            </div>
            <div>
              <label className={labelCls}>Aromă</label>
              <select value={editing.form.aroma} onChange={(e) => setF("aroma", e.target.value)} className={inputCls} data-testid="admin-product-aroma-select">
                <option value="">Fără aromă specificată</option>
                {aromaOptions.map((a) => <option key={a} value={a}>{a}</option>)}
                {editing.form.aroma && !aromaOptions.includes(editing.form.aroma) && (
                  <option value={editing.form.aroma}>{editing.form.aroma} (nedefinită în Conținut site)</option>
                )}
              </select>
              <p className="mt-1 text-xs text-slate-400">Lista de arome o editezi din tab-ul Conținut site — acolo adaugi sau ștergi arome.</p>
            </div>
            <div>
              <label className={labelCls}>Cod produs (SKU)</label>
              <input value={editing.form.sku} onChange={(e) => setF("sku", e.target.value)} className={inputCls} placeholder="Ex: ZL-RF-NEU-2L" data-testid="admin-product-sku-input" />
            </div>
            <div>
              <label className={labelCls}>Preț (lei)</label>
              <input required type="number" step="0.01" min="0" value={editing.form.price} onChange={(e) => setF("price", e.target.value)} className={inputCls} data-testid="admin-product-price-input" />
            </div>
            <div>
              <label className={labelCls}>Preț vechi (opțional)</label>
              <input type="number" step="0.01" min="0" value={editing.form.old_price ?? ""} onChange={(e) => setF("old_price", e.target.value)} className={inputCls} data-testid="admin-product-oldprice-input" />
            </div>
            <div>
              <label className={labelCls}>Stoc</label>
              <input type="number" min="0" value={editing.form.stock} onChange={(e) => setF("stock", e.target.value)} className={inputCls} data-testid="admin-product-stock-input" />
            </div>
            <div>
              <label className={labelCls}>Etichetă (badge)</label>
              <input value={editing.form.tag} onChange={(e) => setF("tag", e.target.value)} className={inputCls} placeholder="Ex: Bestseller, Nou" data-testid="admin-product-tag-input" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Imagine produs</label>
              <ImageField value={editing.form.image} onChange={(v) => setF("image", v)} inputCls={inputCls} testid="admin-product-image-input" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Descriere</label>
              <textarea rows={3} value={editing.form.description} onChange={(e) => setF("description", e.target.value)} className={`${inputCls} resize-none`} data-testid="admin-product-description-input" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Mod de utilizare și dozare</label>
              <textarea rows={2} value={editing.form.usage} onChange={(e) => setF("usage", e.target.value)} className={`${inputCls} resize-none`} placeholder="Ex: Toarnă 50 ml (un capac) pentru o încărcătură normală de 4-5 kg..." data-testid="admin-product-usage-input" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Compoziție (Reg. CE 648/2004)</label>
              <textarea rows={2} value={editing.form.composition} onChange={(e) => setF("composition", e.target.value)} className={`${inputCls} resize-none`} placeholder="Ex: 5-15% agenți tensioactivi anionici, <5% ..." data-testid="admin-product-composition-input" />
            </div>
            <div className="flex flex-col gap-3">
              <label className="flex items-center gap-2.5 text-sm font-semibold text-slate-700">
                <input type="checkbox" checked={editing.form.featured} onChange={(e) => setF("featured", e.target.checked)} className="h-4 w-4 accent-emerald-600" data-testid="admin-product-featured-checkbox" />
                Produs recomandat (apare pe pagina principală)
              </label>
              <label className="flex items-center gap-2.5 text-sm font-semibold text-slate-700">
                <input type="checkbox" checked={!!editing.form.hidden} onChange={(e) => setF("hidden", e.target.checked)} className="h-4 w-4 accent-emerald-600" data-testid="admin-product-hidden-checkbox" />
                Ascuns din magazin (nu apare în catalog)
              </label>
              <label className="flex items-center gap-2.5 text-sm font-semibold text-slate-700">
                <input type="checkbox" checked={!!editing.form.free_shipping} onChange={(e) => setF("free_shipping", e.target.checked)} className="h-4 w-4 accent-emerald-600" data-testid="admin-product-freeshipping-checkbox" />
                Livrare gratuită mereu (ex: pachetul de mostre)
              </label>
            </div>
          </div>
          <button type="submit" className="mt-6 rounded-full bg-slate-900 px-7 py-3 text-sm font-bold text-white transition-colors hover:bg-emerald-600" data-testid="admin-product-save-button">
            Salvează produsul
          </button>
        </form>
      )}

      <div className="mt-5 overflow-x-auto rounded-3xl border border-slate-200/80 bg-white">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-xs uppercase tracking-wider text-slate-400">
              <th className="px-5 py-4">Produs</th>
              <th className="px-5 py-4">Categorie</th>
              <th className="px-5 py-4">Preț</th>
              <th className="px-5 py-4">Stoc</th>
              <th className="px-5 py-4 text-right">Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-slate-50 last:border-0" data-testid={`admin-product-row-${p.id}`}>
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <img src={p.image} alt="" className="h-11 w-11 rounded-xl border border-slate-100 object-cover" />
                    <div>
                      <p className="font-bold text-slate-900">{p.name}</p>
                      <span className="flex flex-wrap items-center gap-2">
                        {p.tag && <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">{p.tag}</span>}
                        {p.hidden && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500" data-testid={`admin-product-hidden-badge-${p.id}`}>Ascuns</span>}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3.5 text-slate-500">{p.category}</td>
                <td className="px-5 py-3.5 font-bold text-slate-900">{fmtPrice(p.price)}</td>
                <td className={`px-5 py-3.5 font-bold ${p.stock <= 0 ? "text-rose-500" : p.stock < 15 ? "text-amber-600" : "text-emerald-600"}`}>{p.stock}</td>
                <td className="px-5 py-3.5">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setEditing({ id: p.id, form: { ...p, old_price: p.old_price ?? "" } })} className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-emerald-700" aria-label="Editează" data-testid={`admin-product-edit-${p.id}`}>
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(p)} className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-rose-100 hover:text-rose-600" aria-label="Șterge" data-testid={`admin-product-delete-${p.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => api.get("/categories").then((r) => setCategories(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) await api.put(`/admin/categories/${editing.id}`, editing.form);
      else await api.post("/admin/categories", editing.form);
      toast.success(editing.id ? "Categorie actualizată" : "Categorie adăugată");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Ștergi categoria „${c.name}"?`)) return;
    await api.delete(`/admin/categories/${c.id}`);
    toast.success("Categorie ștearsă");
    load();
  };

  const setF = (k, v) => setEditing((s) => ({ ...s, form: { ...s.form, [k]: v } }));

  return (
    <div data-testid="admin-categories-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-bold text-slate-900">Categorii ({categories.length})</h3>
        <button
          onClick={() => setEditing({ id: null, form: { ...EMPTY_CATEGORY } })}
          className="flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-xs font-bold text-white transition-colors hover:bg-emerald-700"
          data-testid="admin-category-add-button"
        >
          <Plus className="h-4 w-4" /> Adaugă categorie
        </button>
      </div>

      {editing && (
        <form onSubmit={save} className="mt-5 rounded-3xl border-2 border-emerald-500/40 bg-white p-6" data-testid="admin-category-form">
          <div className="flex items-center justify-between">
            <h4 className="font-display text-base font-bold">{editing.id ? "Editează categoria" : "Categorie nouă"}</h4>
            <button type="button" onClick={() => setEditing(null)} aria-label="Închide" data-testid="admin-category-form-close"><X className="h-5 w-5 text-slate-400" /></button>
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Nume categorie</label>
              <input required value={editing.form.name} onChange={(e) => setF("name", e.target.value)} className={inputCls} data-testid="admin-category-name-input" />
            </div>
            <div>
              <label className={labelCls}>Etichetă (tag)</label>
              <input value={editing.form.tag} onChange={(e) => setF("tag", e.target.value)} className={inputCls} placeholder="Ex: Cele mai populare" data-testid="admin-category-tag-input" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Imagine categorie</label>
              <ImageField value={editing.form.image} onChange={(v) => setF("image", v)} inputCls={inputCls} testid="admin-category-image-input" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Descriere</label>
              <textarea rows={2} value={editing.form.description} onChange={(e) => setF("description", e.target.value)} className={`${inputCls} resize-none`} data-testid="admin-category-description-input" />
            </div>
            <div>
              <label className={labelCls}>Ordine în magazin (număr mic = mai în față)</label>
              <input type="number" value={editing.form.order ?? 99} onChange={(e) => setF("order", parseInt(e.target.value) || 99)} className={inputCls} data-testid="admin-category-order-input" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Instrucțiuni de folosire (apar în magazin la această categorie)</label>
              <textarea rows={3} value={editing.form.usage || ""} onChange={(e) => setF("usage", e.target.value)} className={`${inputCls} resize-none`} placeholder="Ex: Dozează 50 ml per spălare..." data-testid="admin-category-usage-input" />
            </div>
          </div>
          <button type="submit" className="mt-6 rounded-full bg-slate-900 px-7 py-3 text-sm font-bold text-white transition-colors hover:bg-emerald-600" data-testid="admin-category-save-button">
            Salvează categoria
          </button>
        </form>
      )}

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {categories.map((c) => (
          <div key={c.id} className="flex items-center gap-4 rounded-3xl border border-slate-200/80 bg-white p-5" data-testid={`admin-category-row-${c.id}`}>
            <img src={c.image} alt="" className="h-14 w-14 rounded-2xl border border-slate-100 object-cover" />
            <div className="flex-1">
              <p className="font-bold text-slate-900">{c.name}</p>
              <p className="line-clamp-1 text-xs text-slate-400">{c.description}</p>
            </div>
            <button onClick={() => setEditing({ id: c.id, form: { name: c.name, description: c.description, tag: c.tag, image: c.image, usage: c.usage || "", order: c.order ?? 99 } })} className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-emerald-700" aria-label="Editează" data-testid={`admin-category-edit-${c.id}`}>
              <Pencil className="h-4 w-4" />
            </button>
            <button onClick={() => remove(c)} className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-rose-100 hover:text-rose-600" aria-label="Șterge" data-testid={`admin-category-delete-${c.id}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrdersTab() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [awbBusy, setAwbBusy] = useState(null);

  const load = useCallback(() => api.get("/admin/orders").then((r) => setOrders(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const generateAwb = async (o) => {
    setAwbBusy(o.id);
    try {
      const r = await api.post(`/admin/orders/${o.id}/awb`);
      toast.success(`AWB generat: ${r.data.awb_barcode}. Clientul a primit emailul de confirmare.`);
      load();
    } catch (err) {
      toast.error(apiError(err));
      load();
    } finally {
      setAwbBusy(null);
    }
  };

  const downloadLabel = async (o) => {
    setAwbBusy(o.id);
    try {
      const r = await api.get(`/admin/orders/${o.id}/awb/label`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      toast.error("Eticheta nu a putut fi descărcată de la Cargus");
    } finally {
      setAwbBusy(null);
    }
  };

  const setStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/orders/${orderId}`, { status });
      toast.success("Status actualizat");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const removeOrder = async (o) => {
    if (!window.confirm(`Ștergi definitiv comanda ${o.number}? Acțiunea nu poate fi anulată.`)) return;
    try {
      await api.delete(`/admin/orders/${o.id}`);
      toast.success(`Comanda ${o.number} a fost ștearsă`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (orders === null) return <div className="h-64 animate-pulse rounded-3xl bg-slate-100" />;

  return (
    <div data-testid="admin-orders-tab">
      <h3 className="font-display text-lg font-bold text-slate-900">Comenzi ({orders.length})</h3>
      {orders.length === 0 && <p className="mt-4 text-sm text-slate-400">Nicio comandă încă.</p>}
      <div className="mt-5 flex flex-col gap-3">
        {orders.map((o) => (
          <div key={o.id} className="rounded-3xl border border-slate-200/80 bg-white" data-testid={`admin-order-row-${o.id}`}>
            <button
              onClick={() => setExpanded(expanded === o.id ? null : o.id)}
              className="flex w-full flex-wrap items-center justify-between gap-3 px-6 py-4 text-left"
              data-testid={`admin-order-toggle-${o.id}`}
            >
              <div>
                <p className="text-sm font-bold text-slate-900">{o.number} — {o.customer.name}</p>
                <p className="text-xs text-slate-400">
                  {new Date(o.created_at).toLocaleString("ro-RO")} • {o.items.reduce((s, i) => s + i.qty, 0)} produse
                </p>
              </div>
              <div className="flex items-center gap-3">
                {o.awb_barcode && (
                  <span className="rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-bold text-emerald-600">AWB {o.awb_barcode}</span>
                )}
                <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${STATUS_STYLES[o.status]}`}>{STATUS_LABELS[o.status]}</span>
                <span className="text-sm font-extrabold text-slate-900">{fmtPrice(o.total)}</span>
                {user?.role === "admin" && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); removeOrder(o); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); removeOrder(o); } }}
                    className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-colors hover:bg-rose-100 hover:text-rose-600"
                    aria-label={`Șterge comanda ${o.number}`}
                    data-testid={`admin-order-delete-${o.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </span>
                )}
              </div>
            </button>
            {expanded === o.id && (
              <div className="border-t border-slate-100 px-6 py-5" data-testid={`admin-order-details-${o.id}`}>
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="text-sm">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Client</p>
                    <p className="mt-2 font-semibold text-slate-900">{o.customer.name}</p>
                    <p className="text-slate-500">{o.customer.email} • {o.customer.phone}</p>
                    <p className="text-slate-500">{o.customer.address}, {o.customer.city}, {o.customer.county}</p>
                    {o.customer.notes && <p className="mt-1 text-xs italic text-slate-400">„{o.customer.notes}"</p>}
                  </div>
                  <div className="text-sm">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Produse</p>
                    <ul className="mt-2 flex flex-col gap-1">
                      {o.items.map((i) => (
                        <li key={i.product_id} className="flex justify-between text-slate-600">
                          <span>{i.qty}× {i.name}{i.note && <span className="block text-xs italic text-slate-400">{i.note}</span>}</span>
                          <span className="font-semibold">{fmtPrice(i.price * i.qty)}</span>
                        </li>
                      ))}
                      <li className="flex justify-between text-slate-500"><span>Livrare</span><span>{o.shipping === 0 ? "Gratuită" : fmtPrice(o.shipping)}</span></li>
                      <li className="flex justify-between border-t border-slate-100 pt-1.5 font-bold text-slate-900"><span>Total ramburs</span><span>{fmtPrice(o.total)}</span></li>
                    </ul>
                  </div>
                </div>
                {user?.role === "admin" ? (
                  <div className="mt-5 flex items-center gap-3">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-400">Status:</label>
                    <select
                      value={o.status}
                      onChange={(e) => setStatus(o.id, e.target.value)}
                      className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold outline-none focus:border-emerald-500"
                      data-testid={`admin-order-status-${o.id}`}
                    >
                      {ORDER_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                    </select>
                  </div>
                ) : (
                  <p className="mt-5 text-xs font-semibold text-slate-400" data-testid={`admin-order-readonly-${o.id}`}>
                    Cont de vizualizare — statusul comenzilor poate fi schimbat doar de administratorul principal.
                  </p>
                )}
                {user?.role === "admin" && (
                  <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50/70 p-4" data-testid={`admin-order-awb-${o.id}`}>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Livrare Cargus</p>
                    {o.awb_barcode ? (
                      <div className="mt-2 flex flex-wrap items-center gap-3">
                        <span className="rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-bold text-emerald-700" data-testid={`admin-order-awb-badge-${o.id}`}>
                          AWB: {o.awb_barcode}
                        </span>
                        <button
                          onClick={() => downloadLabel(o)}
                          disabled={awbBusy === o.id}
                          className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-bold text-slate-600 transition-colors hover:border-emerald-400 hover:text-emerald-600 disabled:opacity-50"
                          data-testid={`admin-order-awb-label-${o.id}`}
                        >
                          {awbBusy === o.id ? "Se descarcă..." : "Etichetă PDF"}
                        </button>
                      </div>
                    ) : (
                      <div className="mt-2 flex flex-wrap items-center gap-3">
                        <button
                          onClick={() => generateAwb(o)}
                          disabled={awbBusy === o.id}
                          className="rounded-full bg-slate-900 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-emerald-600 disabled:opacity-50"
                          data-testid={`admin-order-awb-generate-${o.id}`}
                        >
                          {awbBusy === o.id ? "Se generează..." : o.awb_status === "eroare" ? "Reîncearcă generarea AWB" : "Confirmă și generează AWB"}
                        </button>
                        {o.awb_status === "eroare" && (
                          <span className="text-xs font-semibold text-rose-500" data-testid={`admin-order-awb-error-${o.id}`}>
                            {o.awb_error || "Ultima încercare a eșuat"}
                          </span>
                        )}
                        {o.awb_status === "neconfigurat" && (
                          <span className="text-xs font-semibold text-amber-600" data-testid={`admin-order-awb-unconfigured-${o.id}`}>
                            Cargus neconfigurat — lipsesc credențialele API
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const CONTENT_DEFAULTS = {
  announcement: "",
  hero_eyebrow: "",
  hero_title: "",
  hero_subtitle: "",
  hero_image: "",
  hero_cta_primary: "Vezi produsele",
  hero_cta_secondary: "Cum se folosește",
  marquee: "",
  figures: [
    { value: "20", label: "de spălări per litru" },
    { value: "50 ml", label: "per spălare — un capac dozator" },
    { value: "2", label: "arome: Ocean Breeze și Floral Fresh" },
  ],
  steps: [
    { title: "Dozezi 50 ml", desc: "Un capac dozator ajunge pentru o încărcătură normală de 4-5 kg. Pentru pete dificile, crește doza la 75 ml." },
    { title: "Toarnă în compartiment", desc: "Adaugi detergentul în compartimentul dedicat al mașinii de spălat, nu direct pe rufe." },
    { title: "Pornești programul", desc: "Alegi programul obișnuit. Formula este eficientă de la 20°C." },
  ],
  arome: [
    { name: "Ocean Breeze", desc: "Un parfum proaspăt de ocean, discret și curat.", image: "" },
    { name: "Floral Fresh", desc: "Un parfum de flori proaspete, care rezistă zile întregi.", image: "" },
  ],
  benefits: [],
  hero_slides: [],
  categories_title: "Tot ce ai nevoie pentru curățenie",
  bestsellers_title: "Cele mai cumpărate produse",
  howto_title: "Trei pași, o singură măsură",
  about_title: "Un detergent care spune exact ce face",
  about_text: "Fără promisiuni exagerate: dozare clară, compoziție declarată și un preț corect per spălare. Comanzi online, plătești la livrare și știi mereu cât îți ajunge o sticlă.",
  about_image: "",
  b2b_title: "Vinzi detergent în magazinul tău?",
  b2b_text: "Oferim prețuri de raft pentru magazine și distribuitori, cu livrare rapidă din stoc. Cere o ofertă și îți răspundem în aceeași zi lucrătoare.",
  b2b_button: "Cere ofertă",
  contact_title: "Întrebări despre produse sau comenzi?",
  contact_text: "Scrie-ne și îți răspundem în cel mai scurt timp.",
  contact_phone: "",
  contact_email: "",
  contact_address: "",
  company_name: "Detergent Studio SRL",
  company_cui: "",
  company_reg: "",
  company_address: "",
  social_facebook: "",
  social_instagram: "",
  social_tiktok: "",
  footer_about: "",
};

function ContentTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);

  const reprocessImages = async () => {
    setReprocessing(true);
    try {
      const r = await api.post("/admin/reprocess-images");
      toast.success(`Gata! ${r.data.fixed} din ${r.data.processed} poze reprocesate fără margini. Reîncarcă pagina ca să le vezi.`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setReprocessing(false);
    }
  };

  useEffect(() => {
    api.get("/content").then((r) => {
      const d = r.data || {};
      setForm({
        ...CONTENT_DEFAULTS,
        ...d,
        marquee: (d.marquee || []).join("\n"),
        figures: (d.figures?.length ? d.figures : CONTENT_DEFAULTS.figures).map((f) => ({ ...f })),
        samples: (d.samples || []).map((s) => ({ name: s.name || "", image: s.image || "" })),
        steps: (d.steps?.length ? d.steps : CONTENT_DEFAULTS.steps).map((s) => ({ ...s })),
        arome: (d.arome?.length ? d.arome : CONTENT_DEFAULTS.arome).map((a) => ({ ...a })),
        benefits: (d.benefits || []).map((b) => ({ ...b })),
        hero_slides: (d.hero_slides || []).map((s) => ({ ...s })),
      });
    });
  }, []);

  if (!form) return <div className="h-64 animate-pulse rounded-lg bg-skytint" />;
  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setArr = (k, i, field, v) =>
    setForm((s) => ({ ...s, [k]: s[k].map((it, idx) => (idx === i ? { ...it, [field]: v } : it)) }));
  const addArr = (k, template) => setForm((s) => ({ ...s, [k]: [...s[k], { ...template }] }));
  const removeArr = (k, i) => setForm((s) => ({ ...s, [k]: s[k].filter((_, idx) => idx !== i) }));

  const addBtnCls = "mt-3 inline-flex items-center gap-2 rounded-full border border-dashed border-slate-300 px-4 py-2 text-xs font-bold text-slate-500 transition-colors hover:border-emerald-400 hover:text-emerald-600";
  const removeBtnCls = "text-slate-300 transition-colors hover:text-rose-500";

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { id, ...rest } = form;
      await api.put("/admin/content", {
        ...rest,
        marquee: form.marquee.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Conținutul site-ului a fost salvat");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const SectionTitle = ({ children }) => (
    <h4 className="border-b border-line pb-2 pt-4 font-display text-sm font-bold uppercase tracking-[0.08em] text-navy">{children}</h4>
  );

  return (
    <form onSubmit={save} className="rounded-lg border border-line bg-white p-6 sm:p-8" data-testid="admin-content-form">
      <h3 className="font-display text-lg font-bold text-slate-900">Conținutul site-ului</h3>
      <p className="mt-1 text-sm text-slate-500">Modifici orice text, buton sau imagine de pe site. Salvarea se vede imediat pe pagină.</p>
      <div className="mt-6 grid gap-5">

        <SectionTitle>Bară de anunț și hero</SectionTitle>
        <div>
          <label className={labelCls}>Bară anunț (sus pe site)</label>
          <input value={form.announcement} onChange={(e) => setF("announcement", e.target.value)} className={inputCls} data-testid="admin-content-announcement-input" />
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Text mic deasupra titlului</label>
            <input value={form.hero_eyebrow} onChange={(e) => setF("hero_eyebrow", e.target.value)} className={inputCls} data-testid="admin-content-eyebrow-input" />
          </div>
          <div>
            <label className={labelCls}>Titlu hero (virgula marchează rândul accentuat)</label>
            <input value={form.hero_title} onChange={(e) => setF("hero_title", e.target.value)} className={inputCls} data-testid="admin-content-title-input" />
          </div>
        </div>
        <div>
          <label className={labelCls}>Subtitlu hero</label>
          <textarea rows={2} value={form.hero_subtitle} onChange={(e) => setF("hero_subtitle", e.target.value)} className={`${inputCls} resize-none`} data-testid="admin-content-subtitle-input" />
        </div>
        <div>
          <label className={labelCls}>Imagine hero de rezervă (apare doar dacă caruselul nu are poze)</label>
          <ImageField value={form.hero_image} onChange={(v) => setF("hero_image", v)} inputCls={inputCls} testid="admin-content-hero-image" />
          <p className="mt-1 text-xs text-slate-400">Lasă gol pentru imaginea implicită (poza cu gama de produse).</p>
        </div>
        <div>
          <label className={labelCls}>Poze carusel hero ({form.hero_slides.length})</label>
          <div className="grid gap-4 sm:grid-cols-2">
            {form.hero_slides.map((s, i) => (
              <div key={i} className="rounded-lg border border-line p-4">
                <div className="flex items-center justify-between">
                  <label className={labelCls}>Poza {i + 1} din carusel</label>
                  {form.hero_slides.length > 1 && (
                    <button type="button" onClick={() => removeArr("hero_slides", i)} className={removeBtnCls} aria-label={`Șterge poza ${i + 2}`} data-testid={`admin-content-slide-${i}-remove`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <ImageField value={s.image} onChange={(v) => setArr("hero_slides", i, "image", v)} inputCls={inputCls} testid={`admin-content-slide-${i}-image`} />
              </div>
            ))}
          </div>
          <button type="button" onClick={() => addArr("hero_slides", { image: "" })} className={addBtnCls} data-testid="admin-content-slide-add">
            <Plus className="h-3.5 w-3.5" /> Adaugă poză în carusel
          </button>
          <p className="mt-1 text-xs text-slate-400">Pozele rulează automat pe prima pagină, peste tot containerul hero. Sunt redimensionate automat la încărcare.</p>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Text buton principal</label>
            <input value={form.hero_cta_primary} onChange={(e) => setF("hero_cta_primary", e.target.value)} className={inputCls} data-testid="admin-content-cta-primary-input" />
          </div>
          <div>
            <label className={labelCls}>Text buton secundar (link)</label>
            <input value={form.hero_cta_secondary} onChange={(e) => setF("hero_cta_secondary", e.target.value)} className={inputCls} data-testid="admin-content-cta-secondary-input" />
          </div>
        </div>

        <SectionTitle>Bandă derulantă și cifre</SectionTitle>
        <div>
          <label className={labelCls}>Bandă derulantă — câte un mesaj pe rând</label>
          <textarea rows={5} value={form.marquee} onChange={(e) => setF("marquee", e.target.value)} className={`${inputCls} resize-none`} data-testid="admin-content-marquee-input" />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {form.figures.map((f, i) => (
            <div key={i} className="rounded-lg border border-line p-4">
              <div className="flex items-center justify-between">
                <label className={labelCls}>Cifra {i + 1}</label>
                {form.figures.length > 1 && (
                  <button type="button" onClick={() => removeArr("figures", i)} className={removeBtnCls} aria-label={`Șterge cifra ${i + 1}`} data-testid={`admin-content-figure-${i}-remove`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <input value={f.value} onChange={(e) => setArr("figures", i, "value", e.target.value)} className={inputCls} placeholder="Ex: 20" data-testid={`admin-content-figure-${i}-value`} />
              <input value={f.label} onChange={(e) => setArr("figures", i, "label", e.target.value)} className={`${inputCls} mt-2`} placeholder="Ex: de spălări per litru" data-testid={`admin-content-figure-${i}-label`} />
            </div>
          ))}
        </div>
        <button type="button" onClick={() => addArr("figures", { value: "", label: "" })} className={addBtnCls} data-testid="admin-content-figure-add">
          <Plus className="h-3.5 w-3.5" /> Adaugă cifră
        </button>

        <SectionTitle>Titluri secțiuni</SectionTitle>
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Titlu secțiune Categorii</label>
            <input value={form.categories_title} onChange={(e) => setF("categories_title", e.target.value)} className={inputCls} data-testid="admin-content-categories-title-input" />
          </div>
          <div>
            <label className={labelCls}>Titlu secțiune Bestsellers</label>
            <input value={form.bestsellers_title} onChange={(e) => setF("bestsellers_title", e.target.value)} className={inputCls} data-testid="admin-content-bestsellers-title-input" />
          </div>
          <div>
            <label className={labelCls}>Titlu „Cum se folosește"</label>
            <input value={form.howto_title} onChange={(e) => setF("howto_title", e.target.value)} className={inputCls} data-testid="admin-content-howto-title-input" />
          </div>
          <div>
            <label className={labelCls}>Titlu secțiune Contact</label>
            <input value={form.contact_title} onChange={(e) => setF("contact_title", e.target.value)} className={inputCls} data-testid="admin-content-contact-title-input" />
          </div>
        </div>
        <div>
          <label className={labelCls}>Text secțiune Contact</label>
          <input value={form.contact_text} onChange={(e) => setF("contact_text", e.target.value)} className={inputCls} data-testid="admin-content-contact-text-input" />
        </div>

        <SectionTitle>Pașii „Cum se folosește" ({form.steps.length})</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-3">
          {form.steps.map((s, i) => (
            <div key={i} className="rounded-lg border border-line p-4">
              <div className="flex items-center justify-between">
                <label className={labelCls}>Pasul {i + 1}</label>
                {form.steps.length > 1 && (
                  <button type="button" onClick={() => removeArr("steps", i)} className={removeBtnCls} aria-label={`Șterge pasul ${i + 1}`} data-testid={`admin-content-step-${i}-remove`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <input value={s.title} onChange={(e) => setArr("steps", i, "title", e.target.value)} className={inputCls} placeholder="Titlu pas" data-testid={`admin-content-step-${i}-title`} />
              <textarea rows={3} value={s.desc} onChange={(e) => setArr("steps", i, "desc", e.target.value)} className={`${inputCls} mt-2 resize-none`} placeholder="Descriere pas" data-testid={`admin-content-step-${i}-desc`} />
            </div>
          ))}
        </div>
        <button type="button" onClick={() => addArr("steps", { title: "", desc: "" })} className={addBtnCls} data-testid="admin-content-step-add">
          <Plus className="h-3.5 w-3.5" /> Adaugă pas
        </button>

        <SectionTitle>Beneficii „De ce să ne alegi pe noi" ({form.benefits.length})</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-2">
          {form.benefits.map((b, i) => (
            <div key={i} className="rounded-lg border border-line p-4">
              <div className="flex items-center justify-between">
                <label className={labelCls}>Beneficiul {i + 1}</label>
                {form.benefits.length > 1 && (
                  <button type="button" onClick={() => removeArr("benefits", i)} className={removeBtnCls} aria-label={`Șterge beneficiul ${i + 1}`} data-testid={`admin-content-benefit-${i}-remove`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <input value={b.title} onChange={(e) => setArr("benefits", i, "title", e.target.value)} className={inputCls} placeholder="Titlu beneficiu" data-testid={`admin-content-benefit-${i}-title`} />
              <textarea rows={2} value={b.desc} onChange={(e) => setArr("benefits", i, "desc", e.target.value)} className={`${inputCls} mt-2 resize-none`} placeholder="Descriere beneficiu" data-testid={`admin-content-benefit-${i}-desc`} />
            </div>
          ))}
        </div>
        <button type="button" onClick={() => addArr("benefits", { title: "", desc: "" })} className={addBtnCls} data-testid="admin-content-benefit-add">
          <Plus className="h-3.5 w-3.5" /> Adaugă beneficiu
        </button>

        <SectionTitle>Arome — plăci pe homepage + filtre în magazin ({form.arome.length})</SectionTitle>
        <p className="rounded-lg bg-skytint px-4 py-3 text-xs text-slate-500">
          Numele aromelor de aici apar automat: ca plăci în secțiunea „Alege aroma potrivită" de pe prima pagină, ca filtre în magazin (după ce intri într-o categorie) și ca opțiuni la editarea produselor. Poți adăuga, edita sau șterge orice aromă.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          {form.arome.map((a, i) => (
            <div key={i} className="rounded-lg border border-line p-4">
              <div className="flex items-center justify-between">
                <label className={labelCls}>Aroma {i + 1}</label>
                {form.arome.length > 1 && (
                  <button type="button" onClick={() => removeArr("arome", i)} className={removeBtnCls} aria-label={`Șterge aroma ${i + 1}`} data-testid={`admin-content-aroma-${i}-remove`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <input value={a.name} onChange={(e) => setArr("arome", i, "name", e.target.value)} className={inputCls} placeholder="Nume aromă" data-testid={`admin-content-aroma-${i}-name`} />
              <input value={a.desc} onChange={(e) => setArr("arome", i, "desc", e.target.value)} className={`${inputCls} mt-2`} placeholder="Descriere aromă" data-testid={`admin-content-aroma-${i}-desc`} />
              <div className="mt-2">
                <ImageField value={a.image} onChange={(v) => setArr("arome", i, "image", v)} inputCls={inputCls} testid={`admin-content-aroma-${i}-image`} />
              </div>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => addArr("arome", { name: "", desc: "", image: "" })} className={addBtnCls} data-testid="admin-content-aroma-add">
          <Plus className="h-3.5 w-3.5" /> Adaugă aromă
        </button>

        <SectionTitle>Mostre — variante din pachetul de 3 ({form.samples.length})</SectionTitle>
        <p className="rounded-lg bg-skytint px-4 py-3 text-xs text-slate-500">
          Acestea sunt variantele pe care clientul le alege în pachetul de 3 mostre de pe prima pagină. Adaugi, editezi sau ștergi orice variantă și îi pui poza ta. Dacă lista e goală, mostrele se generează automat din produsele existente. Prețul (25 lei) și poza pachetului le editezi din tab-ul Produse, la „3 mostre la alegere".
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          {form.samples.map((s, i) => (
            <div key={i} className="rounded-lg border border-line p-4">
              <div className="flex items-center justify-between">
                <label className={labelCls}>Mostra {i + 1}</label>
                <button type="button" onClick={() => removeArr("samples", i)} className={removeBtnCls} aria-label={`Șterge mostra ${i + 1}`} data-testid={`admin-content-sample-${i}-remove`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <input value={s.name} onChange={(e) => setArr("samples", i, "name", e.target.value)} className={inputCls} placeholder="Ex: Detergent de rufe — Ocean Breeze" data-testid={`admin-content-sample-${i}-name`} />
              <div className="mt-2">
                <ImageField value={s.image} onChange={(v) => setArr("samples", i, "image", v)} inputCls={inputCls} testid={`admin-content-sample-${i}-image`} />
              </div>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => addArr("samples", { name: "", image: "" })} className={addBtnCls} data-testid="admin-content-sample-add">
          <Plus className="h-3.5 w-3.5" /> Adaugă mostră
        </button>

        <SectionTitle>Secțiunea „De ce zilnic"</SectionTitle>
        <div>
          <label className={labelCls}>Titlu</label>
          <input value={form.about_title} onChange={(e) => setF("about_title", e.target.value)} className={inputCls} data-testid="admin-content-about-title-input" />
        </div>
        <div>
          <label className={labelCls}>Text</label>
          <textarea rows={3} value={form.about_text} onChange={(e) => setF("about_text", e.target.value)} className={`${inputCls} resize-none`} data-testid="admin-content-about-text-input" />
        </div>
        <div>
          <label className={labelCls}>Imagine secțiune</label>
          <ImageField value={form.about_image} onChange={(v) => setF("about_image", v)} inputCls={inputCls} testid="admin-content-about-image" />
        </div>

        <SectionTitle>Secțiunea „Pentru magazine"</SectionTitle>
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Titlu</label>
            <input value={form.b2b_title} onChange={(e) => setF("b2b_title", e.target.value)} className={inputCls} data-testid="admin-content-b2b-title-input" />
          </div>
          <div>
            <label className={labelCls}>Text buton</label>
            <input value={form.b2b_button} onChange={(e) => setF("b2b_button", e.target.value)} className={inputCls} data-testid="admin-content-b2b-button-input" />
          </div>
        </div>
        <div>
          <label className={labelCls}>Text secțiune</label>
          <textarea rows={2} value={form.b2b_text} onChange={(e) => setF("b2b_text", e.target.value)} className={`${inputCls} resize-none`} data-testid="admin-content-b2b-text-input" />
        </div>

        <SectionTitle>Date firmă (obligatorii legal — apar în footer și în Termeni)</SectionTitle>
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Denumire firmă</label>
            <input value={form.company_name} onChange={(e) => setF("company_name", e.target.value)} className={inputCls} placeholder="Ex: Detergent Studio SRL" data-testid="admin-content-company-name-input" />
          </div>
          <div>
            <label className={labelCls}>CUI / CIF</label>
            <input value={form.company_cui} onChange={(e) => setF("company_cui", e.target.value)} className={inputCls} placeholder="Ex: RO12345678" data-testid="admin-content-company-cui-input" />
          </div>
          <div>
            <label className={labelCls}>Nr. Registrul Comerțului</label>
            <input value={form.company_reg} onChange={(e) => setF("company_reg", e.target.value)} className={inputCls} placeholder="Ex: J40/1234/2026" data-testid="admin-content-company-reg-input" />
          </div>
          <div>
            <label className={labelCls}>Sediu social</label>
            <input value={form.company_address} onChange={(e) => setF("company_address", e.target.value)} className={inputCls} placeholder="Ex: București, Sector 1, Str. ..." data-testid="admin-content-company-address-input" />
          </div>
        </div>

        <SectionTitle>Contact și rețele sociale</SectionTitle>
        <div className="grid gap-5 sm:grid-cols-3">
          <div>
            <label className={labelCls}>Telefon (și WhatsApp)</label>
            <input value={form.contact_phone} onChange={(e) => setF("contact_phone", e.target.value)} className={inputCls} data-testid="admin-content-phone-input" />
          </div>
          <div>
            <label className={labelCls}>Email contact</label>
            <input value={form.contact_email} onChange={(e) => setF("contact_email", e.target.value)} className={inputCls} data-testid="admin-content-email-input" />
          </div>
          <div>
            <label className={labelCls}>Adresă</label>
            <input value={form.contact_address} onChange={(e) => setF("contact_address", e.target.value)} className={inputCls} data-testid="admin-content-address-input" />
          </div>
        </div>
        <div className="grid gap-5 sm:grid-cols-3">
          <div>
            <label className={labelCls}>Link Facebook</label>
            <input value={form.social_facebook} onChange={(e) => setF("social_facebook", e.target.value)} className={inputCls} placeholder="https://facebook.com/..." data-testid="admin-content-facebook-input" />
          </div>
          <div>
            <label className={labelCls}>Link Instagram</label>
            <input value={form.social_instagram} onChange={(e) => setF("social_instagram", e.target.value)} className={inputCls} placeholder="https://instagram.com/..." data-testid="admin-content-instagram-input" />
          </div>
          <div>
            <label className={labelCls}>Link TikTok</label>
            <input value={form.social_tiktok} onChange={(e) => setF("social_tiktok", e.target.value)} className={inputCls} placeholder="https://tiktok.com/@..." data-testid="admin-content-tiktok-input" />
          </div>
        </div>

        <SectionTitle>Subsol</SectionTitle>
        <div>
          <label className={labelCls}>Text „Despre" din subsol</label>
          <textarea rows={2} value={form.footer_about} onChange={(e) => setF("footer_about", e.target.value)} className={`${inputCls} resize-none`} data-testid="admin-content-footer-input" />
        </div>
      </div>
      <div className="mt-8 flex flex-wrap items-center gap-3 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4">
        <button
          type="button"
          onClick={reprocessImages}
          disabled={reprocessing}
          className="rounded-full border border-slate-200 bg-white px-5 py-2.5 text-xs font-bold text-slate-600 transition-colors hover:border-emerald-400 hover:text-emerald-600 disabled:opacity-50"
          data-testid="admin-reprocess-images-button"
        >
          {reprocessing ? "Se repară pozele..." : "Repară pozele încărcate (taie marginile albe)"}
        </button>
        <p className="max-w-md text-xs text-slate-400">
          Reprocesează automat toate pozele încărcate prin admin (carusel hero, arome, produse, imagini de conținut) la standardul full-screen 1000×1000, fără margini albe.
        </p>
      </div>
      <button type="submit" disabled={saving} className="mt-7 rounded-full bg-emerald-600 px-8 py-3.5 text-sm font-bold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60" data-testid="admin-content-save-button">
        {saving ? "Se salvează..." : "Salvează conținutul"}
      </button>
    </form>
  );
}

function MessagesTab() {
  const [messages, setMessages] = useState(null);
  useEffect(() => { api.get("/admin/messages").then((r) => setMessages(r.data)); }, []);
  if (messages === null) return <div className="h-64 animate-pulse rounded-3xl bg-slate-100" />;
  return (
    <div data-testid="admin-messages-tab">
      <h3 className="font-display text-lg font-bold text-slate-900">Mesaje de contact ({messages.length})</h3>
      <div className="mt-5 flex flex-col gap-3">
        {messages.length === 0 && <p className="text-sm text-slate-400">Niciun mesaj încă.</p>}
        {messages.map((m) => (
          <div key={m.id} className="rounded-3xl border border-slate-200/80 bg-white p-6" data-testid={`admin-message-${m.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-bold text-slate-900">{m.name} <span className="font-normal text-slate-400">• {m.email}</span></p>
              <span className="text-xs text-slate-400">{new Date(m.created_at).toLocaleString("ro-RO")}</span>
            </div>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{m.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState(null);

  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)); }, []);

  if (!users) return <p className="py-12 text-center text-sm text-slate-400">Se încarcă conturile...</p>;

  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-6" data-testid="admin-users-tab">
      <h2 className="text-lg font-bold text-slate-900">Conturi înregistrate</h2>
      <p className="mt-1 text-xs text-slate-400">{users.length} conturi create pe site</p>
      <div className="mt-5 overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-xs uppercase tracking-wider text-slate-400">
              <th className="pb-3 font-semibold">Nume</th>
              <th className="pb-3 font-semibold">Email</th>
              <th className="pb-3 font-semibold">Rol</th>
              <th className="pb-3 font-semibold">Înregistrat</th>
              <th className="pb-3 font-semibold">Comenzi</th>
              <th className="pb-3 text-right font-semibold">Total cheltuit</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-50" data-testid={`admin-user-row-${u.id}`}>
                <td className="py-3 font-semibold text-slate-900">{u.name || "—"}</td>
                <td className="py-3 text-slate-600" data-testid={`admin-user-email-${u.id}`}>{u.email}</td>
                <td className="py-3">
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${u.role === "admin" ? "bg-emerald-100 text-emerald-700" : u.role === "operator" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                    {u.role === "admin" ? "Administrator" : u.role === "operator" ? "Operator" : "Client"}
                  </span>
                </td>
                <td className="py-3 text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString("ro-RO") : "—"}</td>
                <td className="py-3 text-slate-600">{u.orders_count}</td>
                <td className="py-3 text-right font-semibold text-slate-900">{u.total_spent ? `${u.total_spent.toFixed(2)} lei` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && <p className="py-10 text-center text-sm text-slate-400">Niciun cont înregistrat încă.</p>}
      </div>
    </div>
  );
}

function WithdrawalsTab() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/withdrawals").then((r) => setItems(r.data)); }, []);
  if (items === null) return <div className="h-64 animate-pulse rounded-3xl bg-slate-100" />;
  return (
    <div data-testid="admin-withdrawals-tab">
      <h3 className="font-display text-lg font-bold text-slate-900">Cereri de retragere din contract ({items.length})</h3>
      <p className="mt-1 text-sm text-slate-500">Depuse prin formularul online de retragere (art. 11¹ OUG 34/2014). Clientul a primit confirmare pe email.</p>
      <div className="mt-5 flex flex-col gap-3">
        {items.length === 0 && <p className="text-sm text-slate-400">Nicio cerere de retragere.</p>}
        {items.map((w) => (
          <div key={w.id} className="rounded-3xl border border-slate-200/80 bg-white p-6" data-testid={`admin-withdrawal-${w.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-bold text-slate-900">{w.name} — comanda <span className="text-navy">{w.order_number}</span></p>
              <span className="text-xs text-slate-400">{new Date(w.created_at).toLocaleString("ro-RO")}</span>
            </div>
            <p className="mt-1 text-sm text-slate-500">{w.email}</p>
            {w.reason && <p className="mt-2 text-sm italic text-slate-500">Motiv: „{w.reason}"</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("stats");

  if (user === undefined) {
    return (
      <main className="mx-auto max-w-7xl px-4 py-24">
        <div className="h-64 animate-pulse rounded-[2rem] bg-slate-100" />
      </main>
    );
  }
  if (!user || !["admin", "operator"].includes(user.role)) return <Navigate to="/autentificare" replace />;

  const visibleTabs = user.role === "admin" ? TABS : TABS.filter((t) => ["stats", "comenzi"].includes(t.id));

  return (
    <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="admin-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-emerald-600">
            <LayoutDashboard className="h-4 w-4" /> DetergentZilnic.ro
          </p>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-slate-900">Panou de administrare</h1>
        </div>
        <button
          onClick={logout}
          className="rounded-full border border-slate-200 px-5 py-2.5 text-xs font-bold text-slate-600 transition-colors hover:border-rose-300 hover:text-rose-500"
          data-testid="admin-logout-button"
        >
          Deconectare
        </button>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-12">
        <aside className="lg:col-span-3">
          <div className="flex gap-2 overflow-x-auto rounded-3xl border border-slate-200/80 bg-white p-2 lg:flex-col" data-testid="admin-tabs">
            {visibleTabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex shrink-0 items-center gap-3 rounded-2xl px-4 py-3 text-sm font-bold transition-all ${tab === t.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                data-testid={`admin-tab-${t.id}`}
              >
                <t.icon className={`h-4 w-4 ${tab === t.id ? "text-emerald-400" : ""}`} />
                {t.label}
              </button>
            ))}
          </div>
        </aside>
        <section className="lg:col-span-9" data-testid="admin-content">
          {tab === "stats" && <StatsTab />}
          {tab === "produse" && <ProductsTab />}
          {tab === "categorii" && <CategoriesTab />}
          {tab === "comenzi" && <OrdersTab />}
          {tab === "retrageri" && <WithdrawalsTab />}
          {tab === "conturi" && <UsersTab />}
          {tab === "continut" && <ContentTab />}
          {tab === "mesaje" && <MessagesTab />}
        </section>
      </div>
    </main>
  );
}
